// @ts-check
// Seletores do editor de modelo de dados: ícone (IconPicker do Twenty, doc 11, 3.6.3) e cor de opção (25 cores, doc 04, 2.4).
import { html } from 'htm/preact';
import { useRef, useState } from 'preact/hooks';
import { Dropdown, DropdownMenuSearchInput } from '../../../ui/dropdown.js';
import { Icon } from '../../../ui/icon.js';
import { ICON_NODES } from '../../../ui/icon-nodes.js';
import { MAIN_COLOR_NAMES, getTagStyle } from '../../../lib/theme-colors.js';
import { t } from '../../../i18n/index.js';

const ICON_NAMES = Object.keys(ICON_NODES).sort();
const MAX_VISIBLE_ICONS = 120;

/**
 * @param {{ value: string | null | undefined, onChange: (icon: string) => void, disabled?: boolean, ariaLabel?: string }} props
 */
export function IconPicker({ value, onChange, disabled = false, ariaLabel }) {
  const anchorRef = useRef(/** @type {HTMLButtonElement | null} */ (null));
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const normalizedSearch = search.trim().toLowerCase();
  const icons = ICON_NAMES.filter((name) => !normalizedSearch || name.toLowerCase().includes(normalizedSearch)).slice(0, MAX_VISIBLE_ICONS);
  return html`<div class="data-model-icon-picker">
    <button type="button" ref=${anchorRef} class="data-model-icon-button" disabled=${disabled} aria-label=${ariaLabel ?? t('dataModel.common.icon')}
      aria-haspopup="dialog" onClick=${() => setIsOpen(!isOpen)}>
      <${Icon} name=${value ?? 'IconCube'} size=${16} />
      <${Icon} name="IconChevronDown" size=${14} className="data-model-icon-button-chevron" />
    </button>
    <${Dropdown} anchorRef=${anchorRef} isOpen=${isOpen} onClose=${() => setIsOpen(false)} width=${296} maxHeight=${360}>
      <${DropdownMenuSearchInput} value=${search} onChange=${setSearch} placeholder=${t('dataModel.common.searchIcon')} />
      <div class="data-model-icon-grid" role="listbox" aria-label=${t('dataModel.common.icon')}>
        ${icons.map((name) => html`<button type="button" key=${name} class="data-model-icon-option" role="option" title=${name}
          aria-label=${name} aria-selected=${name === value ? 'true' : 'false'} data-selected=${name === value ? 'true' : undefined}
          onClick=${() => {
            onChange(name);
            setIsOpen(false);
            setSearch('');
          }}><${Icon} name=${name} size=${16} /></button>`)}
        ${icons.length === 0 ? html`<span class="data-model-empty-hint">${t('common.noResults')}</span>` : null}
      </div>
    <//>
  </div>`;
}

/**
 * Bolinha de cor que abre a paleta.
 * @param {{ value: string | undefined, onChange: (color: string) => void, disabled?: boolean, ariaLabel: string }} props
 */
export function ColorPicker({ value, onChange, disabled = false, ariaLabel }) {
  const anchorRef = useRef(/** @type {HTMLButtonElement | null} */ (null));
  const [isOpen, setIsOpen] = useState(false);
  return html`<span class="data-model-color-picker">
    <button type="button" ref=${anchorRef} class="data-model-color-swatch" style=${getTagStyle(value)} disabled=${disabled}
      aria-label=${ariaLabel} title=${ariaLabel} onClick=${() => setIsOpen(!isOpen)}></button>
    <${Dropdown} anchorRef=${anchorRef} isOpen=${isOpen} onClose=${() => setIsOpen(false)} width=${232}>
      <div class="data-model-color-grid" role="listbox" aria-label=${t('dataModel.options.color')}>
        ${MAIN_COLOR_NAMES.map((color) => html`<button type="button" key=${color} class="data-model-color-option" role="option"
          style=${getTagStyle(color)} title=${t(`dataModel.colors.${color}`)} aria-label=${t(`dataModel.colors.${color}`)}
          aria-selected=${color === value ? 'true' : 'false'} data-selected=${color === value ? 'true' : undefined}
          onClick=${() => {
            onChange(color);
            setIsOpen(false);
          }}>${color === value ? html`<${Icon} name="IconCheck" size=${12} />` : null}</button>`)}
      </div>
    <//>
  </span>`;
}
